// «الشريط المتحرك» — رسالة المالك تمشي أمام الجميع وهم يعملون (0071).
//
// **سطرٌ واحد لا صفحة**: شريطٌ رفيع تحت شريط التنقّل، نصُّه يبدأ من أقصى اليسار ويمشي يمينًا
// هادئًا. يقف عند مرور الماوس أو التركيز بلوحة المفاتيح، فمن أراد قراءته على مهل وقف له.
//
// **ويمشي دائمًا** (قرار المالك 23/09/2026: «الشريط ده عند رحمه توقّف، اتأكد إنه بيتحرّك»):
// كان يقف بإعداد «تقليل الحركة» في نظام التشغيل — وهو مفعَّلٌ على أجهزةٍ كثيرة بلا علمِ
// أصحابها — وكان يقف بالتركيز: نقرةٌ على الرابط تُبقي التركيزَ عليه فيقف الشريط حتى يُنقر
// غيرُه. فلم يبقَ إلا وقفةُ الماوس فوقه، وهي تنتهي بمجرّد أن يُزاح.
//
// ولا يظهر إلا حين تكون هناك رسالةٌ **تعمل**: لا شريط فارغ ولا مساحة محجوزة بلا سبب.
// وإخفاؤه قرارُ من يقرأ، ويعود بأول تحديث للصفحة.
//
// **ويتحدَّث فورًا عند الحفظ** (قرار المالك): بلا هذا كان الأدمن يحفظ رسالةً جديدة فيرى
// الشريط أمامه لا يزال يعرض القديمة (الدورة كل خمس دقائق)، فيظنّ أن الحفظ لم يعمل ويحفظ
// مرّة أخرى — فتتكرّر نفس النسخة في السجل بلا داعٍ. الحلّ: TickerButton يبثّ حدثًا على
// window بعد كل حفظ ناجح، وكل شريطٍ مفتوح على الشاشة يسمعه ويُحدَّث في نفس اللحظة.
import { useCallback, useEffect, useState } from 'react';
import { get, type TickerMessage } from '../lib/api.ts';

/** كل خمس دقائق: رسالةٌ يكتبها المالك تصل الشاشات بلا أن يُعاد تحميلها. */
const REFRESH_MS = 5 * 60 * 1000;

/**
 * النجمة **فاصلٌ** بين رسالةٍ وأخرى لا حرفًا يُقرأ (قرار المالك): يكتب المالك في المربّع
 * الواحد «عرضٌ ★ تنبيهٌ ★ تذكير»، فتمشي الثلاثة على الشريط منفصلةً بنجمةٍ ومسافةٍ أمام
 * كلٍّ منها — بدل سطرٍ طويلٍ تختلط فيه الجُمل فلا يُعرف أين تنتهي واحدة وتبدأ التالية.
 *
 * ويُقبل شكلا النجمة معًا (★ و⭐): من يكتبها بلوحة المفاتيح ومن يُدرجها من لوحة الرموز
 * كلاهما يقصد الفصل نفسه، فلا تظهر عند أحدهما حرفًا عالقًا وسط الكلام.
 */
function segmentsOf(body: string): string[] {
  const parts = body.split(/[★⭐]+/).map(s => s.trim()).filter(s => s !== '');
  return parts.length > 0 ? parts : [body.trim()];
}

/** اسم الحدث الذي يبثّه TickerButton بعد كل حفظ ناجح — انظر التعليق أعلاه. */
export const TICKER_SAVED_EVENT = 'icontravel:ticker-saved';

export function TickerBar() {
  const [data, setData] = useState<TickerMessage | null>(null);
  const [hidden, setHidden] = useState(false);

  const load = useCallback(async () => {
    try { setData(await get<TickerMessage>('/ticker')); }
    catch { /* الشريط زينةٌ لا وظيفة: فشلُه لا يُعرَض خطأً ولا يُعطّل شاشة */ }
  }, []);

  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), REFRESH_MS);
    // حفظٌ ناجح في أي مكان بالصفحة يُحدَّث فورًا هنا — لا ينتظر الدورة، ولا يبقى مخفيًّا
    // بقرار قديم: رسالة جديدة تستحقّ أن تُرى.
    const onSaved = () => { setHidden(false); void load(); };
    window.addEventListener(TICKER_SAVED_EVENT, onSaved);
    return () => { clearInterval(t); window.removeEventListener(TICKER_SAVED_EVENT, onSaved); };
  }, [load]);

  if (hidden || !data?.active || data.body.trim() === '') return null;

  // المسافة بين رسالتين في نفس الدورة (ms-24) أصغر من المسافة بين الدورة وتكرارها (px-64):
  // فيُقرأ الفاصلان مختلفَين — هذه رسالةٌ تالية، وتلك بدايةُ الشريط من جديد.
  const parts = segmentsOf(data.body);
  const Body = () => (
    <span className="px-64">
      {parts.map((part, i) => (
        <span key={i} className={i > 0 ? 'ms-24' : undefined}>
          <span aria-hidden="true" className="me-3 text-[#f7941e]">★</span>
          {part}
        </span>
      ))}
    </span>
  );

  return (
    // ألوان الشعار نفسه لا أزرق الهوية (الترويسة والتنقّل والأزرار زرقاء فيذوب فيها أيّ
    // أزرق): الفيروزي هو لون دائرة الشعار، والبرتقالي هو لون اسم «iCon» فيه — نفس التركيبة.
    <div dir="rtl" role="region" aria-label="إعلان الشركة"
      className="relative overflow-hidden border-b border-[#0e6e7c]/40 bg-gradient-to-l from-[#0b5c68] via-[#12879a] to-[#1aa8bd] text-white shadow-sm">
      {/* لمعةٌ خفيفة تمرّ على الشريط — إحساسٌ راقٍ بلا وميض يزعج */}
      <span aria-hidden="true" className="ticker-sheen pointer-events-none absolute inset-y-0 w-1/3" />

      <div className="relative flex items-center gap-2.5 px-3 py-0.5 sm:px-6">
        <span className="hidden shrink-0 items-center gap-1.5 rounded-full bg-[#f7941e] px-2.5 py-0.5 text-[0.65rem] font-bold tracking-wide text-white ring-1 ring-inset ring-white/40 sm:inline-flex">
          <svg viewBox="0 0 24 24" aria-hidden="true" className="h-3.5 w-3.5"
            fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3.5 10.5v3a1.5 1.5 0 0 0 1.5 1.5h2l5 4V5l-5 4H5a1.5 1.5 0 0 0-1.5 1.5z" />
            <path d="M16.5 9.5a4 4 0 0 1 0 5M19 7a7.5 7.5 0 0 1 0 10" />
          </svg>
          إعلان
        </span>

        {/*
         * المسار: يُخفي ما خرج، والنصّ داخله يمشي — ويقف عند مرور الماوس وحده (لا بالتركيز:
         * التركيزُ يبقى بعد النقر فيقف الشريط بلا سبب ظاهر — قرار المالك 23/09/2026).
         *
         * `dir="ltr"` هنا فقط لتثبيت **بداية** النصّ من أقصى اليسار (قرار المالك): بلا هذا
         * كان الصندوق يلتصق يمين الحاوية افتراضيًا (لأن الأب `dir="rtl"`)، فيبدو النصّ
         * كأنه يبدأ من المنتصف حين يكون أقصر من عرض الشريط. والعربية داخله تبقى تُقرأ صحيحة
         * الاتجاه — خوارزمية Unicode Bidi ترسم كل كلمة عربية من اليمين لليسار مهما كان اتجاه
         * الحاوية، وهذا التأثير على ترتيب الصندوق نفسه لا على شكل الحروف.
         */}
        <div className="group relative min-w-0 flex-1 overflow-hidden" dir="ltr">
          {/* السرعة إعدادٌ يكتبه الأدمن مع الرسالة (0072) لا رقمٌ ثابت في التنسيق — الأسلوب
              المضمَّن هنا يتفوّق على مدّة الحركة الافتراضية في index.css (18 ثانية fallback
              حين لا قيمة، وهو نفسه الافتراضي المخزَّن). */}
          <div className="ticker-lane flex w-max items-center whitespace-nowrap text-sm font-medium
                          group-hover:[animation-play-state:paused]"
            style={{ animationDuration: `${data.speedSeconds ?? 18}s` }}>
            {data.link ? (
              <a href={data.link} target="_blank" rel="noopener noreferrer"
                className="rounded outline-none hover:underline focus-visible:ring-2 focus-visible:ring-white/70">
                <Body />
              </a>
            ) : <Body />}
            {/* نسخةٌ واحدة لا نسختان (قرار المالك 05/10/2026: «عاوز الكلام يمشى فيه من اقصى الشمال لاقصى اليمين، انما دلوقتى
                الكلام بيظهر من النص»): كانت النسختان تملآن الشريط من أوّل لحظة فيبدو النصّ ظاهرًا من المنتصف. الآن يدخل
                من حافّة اليسار ويخرج من حافّة اليمين كاملًا ثم يعود — الحركة في index.css (.ticker-lane). */}
          </div>
        </div>

        <button type="button" aria-label="إخفاء الشريط"
          className="shrink-0 rounded px-1.5 py-0.5 text-white/70 outline-none transition hover:bg-white/20 hover:text-white focus-visible:ring-2 focus-visible:ring-white/70"
          onClick={() => setHidden(true)}>
          ✕
        </button>
      </div>
    </div>
  );
}
